import { tsToDate, startOfDay, dayDelta, recordAmount, recordStageId, engagementType, ENGAGEMENT_TYPES } from './format.js'
export { startOfDay, dayDelta }

export const isTaskDone = (t, statusesById) => !!statusesById[t.status]?.done

export const isOverdueTask = (t, statusesById, today = new Date()) => {
  if (isTaskDone(t, statusesById)) return false
  const d = tsToDate(t.dueDate)
  return !!d && dayDelta(d, today) < 0
}

/** Open, has a due date, and that date falls in [today, today + days]. */
export const isDueWithinDays = (t, statusesById, days, today = new Date()) => {
  if (isTaskDone(t, statusesById)) return false
  const d = tsToDate(t.dueDate)
  if (!d) return false
  const delta = dayDelta(d, today)
  return delta >= 0 && delta <= days
}

export function taskMetrics(tasks = [], statusesById = {}, { uid, today = new Date() } = {}) {
  const mineAll = tasks.filter((t) => t.assigneeUid === uid)
  const openMine = mineAll.filter((t) => !isTaskDone(t, statusesById))
  const dueSoon = openMine.filter((t) => isDueWithinDays(t, statusesById, 14, today)).length
  const byStatusMap = {}
  openMine.forEach((t) => { byStatusMap[t.status] = (byStatusMap[t.status] || 0) + 1 })
  const byStatus = Object.keys(byStatusMap).map((id) => ({ id, name: statusesById[id]?.name || id, color: statusesById[id]?.color || '#7A7568', count: byStatusMap[id] }))
  const nextDue = openMine.filter((t) => tsToDate(t.dueDate)).sort((a, b) => tsToDate(a.dueDate) - tsToDate(b.dueDate)).slice(0, 5)
  const byPhase = { p1: 0, p2: 0, p3: 0 }
  tasks.forEach((t) => { if (byPhase[t.phase] !== undefined) byPhase[t.phase] += 1 })
  return {
    mine: { open: openMine.length, dueSoon, overdue: mineAll.filter((t) => isOverdueTask(t, statusesById, today)).length, byStatus, nextDue },
    workspace: {
      open: tasks.filter((t) => !isTaskDone(t, statusesById)).length,
      done: tasks.filter((t) => isTaskDone(t, statusesById)).length,
      overdue: tasks.filter((t) => isOverdueTask(t, statusesById, today)).length,
      byPhase,
    },
  }
}

/**
 * Pipeline metrics over ENGAGEMENTS (Wave 2). Previously `dealMetrics` over the
 * `deals` mirror; the shape of the return value is deliberately unchanged so the
 * Home dashboard Kaycia praised — and every Wave 1 drill-down link on it — keeps
 * working. Only the input collection changed.
 *
 * "Won" is now read from the stage's `outcome`, not a `dealStatus` field: with one
 * status set per type, the terminal stage IS the outcome.
 */
export function pipelineMetrics(records = [], stages = [], { today = new Date() } = {}) {
  const stageById = Object.fromEntries(stages.map((s) => [s.id, s]))
  const stageOf = (r) => stageById[recordStageId(r)]
  const isOpen = (r) => !stageOf(r)?.outcome
  let rawOpen = 0, weightedOpen = 0, openCount = 0
  records.forEach((r) => {
    if (!isOpen(r)) return
    const amt = recordAmount(r)
    openCount += 1
    rawOpen += amt
    weightedOpen += amt * ((stageOf(r)?.probability || 0) / 100)
  })
  const ym = today.getUTCFullYear() * 12 + today.getUTCMonth()
  const won = records.filter((r) => stageOf(r)?.outcome === 'won' && (() => {
    const u = tsToDate(r.updatedAt)
    return u && (u.getUTCFullYear() * 12 + u.getUTCMonth()) === ym
  })())
  const byStageMap = {}
  records.forEach((r) => {
    const sid = recordStageId(r)
    if (!byStageMap[sid]) byStageMap[sid] = { count: 0, raw: 0 }
    byStageMap[sid].count += 1
    byStageMap[sid].raw += recordAmount(r)
  })
  const byStage = stages.map((s) => ({ id: s.id, name: s.name, color: s.color || '#7A7568', count: byStageMap[s.id]?.count || 0, raw: byStageMap[s.id]?.raw || 0 }))
  return { openCount, rawOpen, weightedOpen, wonThisMonth: { count: won.length, amount: won.reduce((n, r) => n + recordAmount(r), 0) }, byStage }
}

export function engagementMetrics(engagements = [], stages = []) {
  const mk = (type) => stages.filter((s) => s.type === type).sort((a, b) => a.order - b.order)
    .map((s) => ({ stageId: s.id, name: s.name, count: engagements.filter((e) => engagementType(e) === type && e.stage === s.id).length }))
  const confirmedFees = engagements.reduce((n, e) => n + ((e.feeStatus === 'confirmed' || e.feeStatus === 'paid') && typeof e.fee === 'number' ? e.fee : 0), 0)
  const byType = Object.fromEntries(ENGAGEMENT_TYPES.map((t) => [t, mk(t)]))
  return { byType, confirmedFees }
}

export function crmMetrics(contacts = [], companies = []) {
  return { contacts: contacts.length, companies: companies.length }
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
export function monthlyCounts(docs = [], { months = 6, today = new Date(), dateField = 'createdAt' } = {}) {
  const base = today.getUTCFullYear() * 12 + today.getUTCMonth()
  const buckets = []
  for (let i = months - 1; i >= 0; i--) { const m = base - i; buckets.push({ ym: `${Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, '0')}`, label: MONTHS[m % 12], count: 0 }) }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.ym, i]))
  docs.forEach((d) => { const dt = tsToDate(d[dateField]); if (!dt) return; const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`; if (idx[key] !== undefined) buckets[idx[key]].count += 1 })
  return buckets
}
